import React, { useState, useEffect } from "react";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";

/* ─── Helpers ─────────────────────────────────────────────────────────────── */
const empName = (emp) => {
  if (emp.firstName || emp.lastName) return `${emp.firstName || ""} ${emp.lastName || ""}`.trim();
  return emp.name || emp.fullName || emp.username || `#${emp.id}`;
};
const initials = (name = "") => name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2) || "?";
const maskToken = (tok = "") => tok.length <= 8 ? "••••••••" : tok.slice(0, 6) + "••••••••" + tok.slice(-4);

/* ─── Styles ──────────────────────────────────────────────────────────────── */
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

  :root {
    --bg: #f0f2f7;
    --surface: #ffffff;
    --surface2: #f7f9fc;
    --surface3: #edf0f7;
    --border: #e1e6ef;
    --border-focus: #3b82f6;
    --accent: #2563eb;
    --accent-light: #eff6ff;
    --success: #16a34a;
    --success-light: #f0fdf4;
    --danger: #dc2626;
    --danger-light: #fef2f2;
    --warning: #d97706;
    --warning-light: #fffbeb;
    --text: #0f172a;
    --text-muted: #64748b;
    --text-dim: #94a3b8;
    --shadow-sm: 0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04);
    --shadow-md: 0 4px 16px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.04);
    --shadow-xl: 0 24px 56px rgba(0,0,0,0.14), 0 8px 16px rgba(0,0,0,0.08);
    --navbar-h: 90px;
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }

  .ei-page {
    height: 100vh;
    background: var(--bg);
    padding-top: var(--navbar-h);
    display: flex;
    flex-direction: column;
    color: var(--text);
    overflow: hidden;
  }

  .ei-content {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 1.75rem 2.5rem 2.5rem;
    display: flex;
    flex-direction: column;
    width: 100%;
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 transparent;
  }
  .ei-content::-webkit-scrollbar { width: 6px; }
  .ei-content::-webkit-scrollbar-track { background: transparent; }
  .ei-content::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }

  .ei-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.25rem;
    flex-shrink: 0;
  }
  .ei-header-left { display: flex; align-items: center; gap: .9rem; }
  .ei-header-icon {
    width: 44px; height: 44px;
    background: #dbeafe;
    border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    font-size: 1.25rem;
    flex-shrink: 0;
  }
  .ei-header h4 { font-size: 1.3rem; font-weight: 700; margin: 0 0 .2rem; color: var(--text); }
  .ei-header p { color: var(--text-muted); margin: 0; font-size: .83rem; }

  .ei-stats { display: flex; gap: .85rem; margin-bottom: 1.25rem; flex-shrink: 0; }
  .stat-chip {
    background: var(--surface); border: 1px solid var(--border);
    border-radius: 8px; padding: .5rem .9rem; font-size: .78rem; font-weight: 500;
    color: var(--text-muted); display: flex; align-items: center; gap: .4rem;
    box-shadow: var(--shadow-sm);
  }
  .stat-chip strong { color: var(--text); font-size: .9rem; font-weight: 700; }

  .btn-back {
    display: inline-flex; align-items: center; gap: .4rem;
    background: var(--surface); color: var(--text-muted);
    border: 1px solid var(--border); border-radius: 9px;
    padding: .5rem .9rem; font-family: 'DM Sans', sans-serif;
    font-weight: 600; font-size: .82rem; cursor: pointer; transition: all .15s; flex-shrink: 0;
  }
  .btn-back:hover { background: var(--surface2); color: var(--text); border-color: #cbd5e1; }

  .btn-new {
    display: inline-flex; align-items: center; gap: .5rem;
    background: var(--accent); color: #fff; border: none; border-radius: 10px;
    padding: .62rem 1.3rem; font-family: 'DM Sans', sans-serif;
    font-weight: 700; font-size: .86rem; cursor: pointer;
    box-shadow: 0 2px 8px rgba(37,99,235,.25); transition: all .15s;
    white-space: nowrap; flex-shrink: 0;
  }
  .btn-new:hover { background: #1d4ed8; transform: translateY(-1px); box-shadow: 0 4px 14px rgba(37,99,235,.32); }

  .ei-empty {
    background: var(--surface); border: 1px dashed var(--border); border-radius: 16px;
    text-align: center; padding: 5rem 2rem; color: var(--text-muted);
    box-shadow: var(--shadow-sm); flex: 1;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
  }
  .ei-empty-icon { font-size: 3rem; margin-bottom: 1rem; opacity: .4; }
  .ei-empty h6 { color: var(--text); font-weight: 700; margin-bottom: .4rem; font-size: 1.05rem; }
  .ei-empty p { font-size: .86rem; max-width: 340px; line-height: 1.6; }

  .ei-table-card {
    background: var(--surface); border: 1px solid var(--border);
    border-radius: 16px; box-shadow: var(--shadow-sm); overflow: hidden;
    flex: 1; min-height: 0; display: flex; flex-direction: column;
  }
  .ei-table-scroll {
    overflow: auto; flex: 1; min-height: 0;
    scrollbar-width: thin; scrollbar-color: #cbd5e1 transparent;
  }
  .ei-table-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
  .ei-table-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }

  .ei-table { width: 100%; border-collapse: collapse; min-width: 700px; }
  .ei-table thead {
    background: var(--surface2); border-bottom: 2px solid var(--border);
    position: sticky; top: 0; z-index: 2;
  }
  .ei-table thead th {
    padding: .9rem 1.25rem; font-size: .7rem; font-weight: 700;
    letter-spacing: .08em; text-transform: uppercase; color: var(--text-muted);
    text-align: left; white-space: nowrap; background: var(--surface2);
  }
  .ei-table thead th:first-child { padding-left: 1.5rem; }
  .ei-table thead th:last-child { padding-right: 1.5rem; }
  .ei-table tbody tr { border-bottom: 1px solid var(--border); transition: background .1s; }
  .ei-table tbody tr:last-child { border-bottom: none; }
  .ei-table tbody tr:hover { background: #f8fbff; }
  .ei-table tbody td { padding: 1rem 1.25rem; font-size: .86rem; color: var(--text); vertical-align: middle; }
  .ei-table tbody td:first-child { padding-left: 1.5rem; }
  .ei-table tbody td:last-child { padding-right: 1.5rem; }

  .td-url { font-family: 'DM Mono', monospace; font-size: .78rem; color: var(--text-muted); max-width: 220px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: block; }
  .td-token { font-family: 'DM Mono', monospace; font-size: .78rem; color: var(--text-muted); letter-spacing: .08em; }
  .td-badge { display: inline-flex; align-items: center; gap: .35rem; font-size: .84rem; font-weight: 600; color: var(--text); }
  .td-badge-dot { width: 24px; height: 24px; background: var(--accent-light); color: var(--accent); border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: .72rem; font-weight: 700; }
  .td-sno { display: inline-flex; align-items: center; padding: 2px 10px; border-radius: 100px; font-size: .72rem; font-weight: 600; background: var(--accent-light); color: var(--accent); }

  .row-actions { display: flex; gap: .4rem; align-items: center; flex-wrap: nowrap; }
  .btn-action {
    border-radius: 8px; padding: .38rem .75rem; font-size: .78rem;
    font-family: 'DM Sans', sans-serif; font-weight: 600; cursor: pointer;
    border: 1px solid var(--border); background: transparent; color: var(--text-muted);
    transition: all .13s; display: inline-flex; align-items: center; gap: .3rem; white-space: nowrap;
  }
  .btn-action:hover { background: var(--surface2); color: var(--text); border-color: #cbd5e1; }
  .btn-action.edit:hover { background: var(--accent-light); color: var(--accent); border-color: #bfdbfe; }
  .btn-action.del:hover { background: var(--danger-light); color: var(--danger); border-color: #fecaca; }

  .ei-table-footer {
    padding: .75rem 1.5rem; border-top: 1px solid var(--border);
    background: var(--surface2); font-size: .78rem; color: var(--text-muted);
    font-weight: 500; flex-shrink: 0;
  }

  /* ── Modal ── */
  .modal-backdrop {
    position: fixed; inset: 0; background: rgba(15,23,42,.5);
    backdrop-filter: blur(5px); z-index: 1050;
    display: flex; align-items: center; justify-content: center;
    padding: 1.5rem; overflow: hidden; animation: mFadeIn .16s ease;
  }
  @keyframes mFadeIn { from { opacity: 0 } to { opacity: 1 } }
  .modal-box {
    background: var(--surface); border: 1px solid var(--border); border-radius: 20px;
    width: 100%; max-width: 680px; max-height: 90vh;
    box-shadow: var(--shadow-xl);
    animation: mSlideUp .2s cubic-bezier(.22,.61,.36,1);
    display: flex; flex-direction: column;
  }
  @keyframes mSlideUp { from { opacity:0; transform:translateY(24px) scale(0.97) } to { opacity:1; transform:none } }
  .modal-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 1.4rem 1.75rem; border-bottom: 1px solid var(--border); flex-shrink: 0;
  }
  .modal-header-left { display: flex; align-items: center; gap: .8rem; }
  .modal-icon-wrap {
    width: 40px; height: 40px; background: var(--accent-light); border-radius: 11px;
    display: flex; align-items: center; justify-content: center; font-size: 1.1rem; flex-shrink: 0;
  }
  .modal-title { font-weight: 700; font-size: 1.05rem; color: var(--text); }
  .modal-subtitle { font-size: .76rem; color: var(--text-muted); margin-top: .1rem; }
  .modal-close {
    background: transparent; border: 1px solid var(--border); color: var(--text-muted);
    border-radius: 8px; width: 32px; height: 32px;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; font-size: 1rem; transition: all .13s; line-height: 1; flex-shrink: 0;
  }
  .modal-close:hover { border-color: #fca5a5; color: var(--danger); background: var(--danger-light); }
  .modal-body {
    padding: 1.6rem 1.75rem; overflow-y: auto; flex: 1; min-height: 0;
    scrollbar-width: thin; scrollbar-color: #cbd5e1 transparent;
  }
  .modal-body::-webkit-scrollbar { width: 5px; }
  .modal-body::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
  .modal-footer {
    padding: 1.1rem 1.75rem; border-top: 1px solid var(--border);
    display: flex; align-items: center; justify-content: flex-end; gap: .65rem;
    background: var(--surface2); border-radius: 0 0 20px 20px; flex-shrink: 0;
  }

  .form-section { margin-bottom: 1.6rem; }
  .form-section-label {
    font-size: .7rem; font-weight: 700; letter-spacing: .1em; text-transform: uppercase;
    color: var(--text-muted); margin-bottom: .9rem;
    display: flex; align-items: center; gap: .55rem;
  }
  .form-section-label::after { content: ''; flex: 1; height: 1px; background: var(--border); }
  .form-row { display: flex; gap: .8rem; flex-wrap: wrap; margin-bottom: .8rem; }
  .form-row:last-child { margin-bottom: 0; }
  .fg { display: flex; flex-direction: column; gap: .38rem; flex: 1; min-width: 160px; }
  .fg.full { flex: 0 0 100%; min-width: 0; }
  .fg.half { flex: 0 0 calc(50% - .4rem); min-width: 0; }
  .form-label { font-size: .78rem; font-weight: 600; color: var(--text); }
  .form-label .req { color: var(--danger); margin-left: 2px; }
  .form-hint { font-size: .71rem; color: var(--text-muted); }
  .f-control, .f-select {
    background: var(--surface) !important; border: 1px solid var(--border) !important;
    border-radius: 9px !important; color: var(--text) !important;
    font-family: 'DM Sans', sans-serif; font-size: .86rem;
    padding: .58rem .85rem; transition: border-color .14s, box-shadow .14s;
    width: 100%; outline: none;
  }
  .f-control:focus, .f-select:focus {
    border-color: var(--border-focus) !important;
    box-shadow: 0 0 0 3px rgba(59,130,246,.12) !important;
  }
  .f-control::placeholder { color: var(--text-dim); }
  .f-token-wrap { position: relative; }
  .f-token-wrap .f-control { padding-right: 72px; font-family: 'DM Mono', monospace; font-size: .82rem; letter-spacing: .03em; }
  .f-token-toggle {
    position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
    background: var(--accent-light); color: var(--accent); border: none;
    border-radius: 6px; padding: 4px 9px; font-size: .72rem; font-weight: 600;
    cursor: pointer; font-family: 'DM Sans', sans-serif; transition: all .15s;
  }
  .f-token-toggle:hover { background: var(--accent); color: #fff; }

  /* Employee chips */
  .emp-chip-list { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; min-height: 36px; }
  .emp-chip {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 5px 12px 5px 6px; background: var(--accent-light); color: var(--accent);
    border-radius: 100px; font-size: .78rem; font-weight: 500;
    border: 1.5px solid rgba(37,99,235,.2); cursor: pointer;
    transition: all .15s; user-select: none;
  }
  .emp-chip.selected { background: var(--accent); color: #fff; border-color: #1d4ed8; box-shadow: 0 2px 8px rgba(37,99,235,.3); }
  .emp-chip:hover:not(.selected) { background: rgba(37,99,235,.12); }
  .emp-avatar { width: 22px; height: 22px; border-radius: 50%; background: rgba(255,255,255,.35); display: flex; align-items: center; justify-content: center; font-size: .68rem; font-weight: 700; flex-shrink: 0; }
  .emp-select-actions { display: flex; gap: 8px; margin-bottom: 4px; }
  .emp-sel-btn {
    display: inline-flex; align-items: center; gap: 5px; padding: 4px 12px;
    border-radius: 7px; font-size: .76rem; font-weight: 600; cursor: pointer;
    border: 1.5px solid var(--accent); background: var(--accent-light);
    color: var(--accent); font-family: 'DM Sans', sans-serif; transition: all .15s;
  }
  .emp-sel-btn:hover { background: var(--accent); color: #fff; }
  .emp-sel-btn.desel { background: var(--surface2); color: var(--text-muted); border-color: var(--border); }
  .emp-sel-btn.desel:hover { background: var(--surface3); color: var(--text); }

  .btn-primary-m {
    background: var(--accent); color: #fff; border: none; border-radius: 10px;
    padding: .62rem 1.5rem; font-family: 'DM Sans', sans-serif; font-weight: 700; font-size: .86rem;
    cursor: pointer; box-shadow: 0 2px 10px rgba(37,99,235,.25);
    transition: background .14s, box-shadow .14s, transform .1s;
    display: inline-flex; align-items: center; gap: .45rem;
  }
  .btn-primary-m:hover:not(:disabled) { background: #1d4ed8; transform: translateY(-1px); box-shadow: 0 4px 16px rgba(37,99,235,.3); }
  .btn-primary-m:disabled { opacity: .6; cursor: not-allowed; }
  .btn-cancel-m {
    background: var(--surface); color: var(--text-muted); border: 1px solid var(--border);
    border-radius: 10px; padding: .62rem 1.2rem; font-family: 'DM Sans', sans-serif;
    font-weight: 600; font-size: .86rem; cursor: pointer; transition: all .13s;
  }
  .btn-cancel-m:hover { background: var(--surface2); color: var(--text); border-color: #cbd5e1; }

  @keyframes spin { to { transform: rotate(360deg); } }
  .spinner { width: 13px; height: 13px; border: 2px solid rgba(255,255,255,.3); border-top-color: #fff; border-radius: 50%; animation: spin .65s linear infinite; display: inline-block; }

  /* ── View Modal ── */
  .view-modal-box {
    background: var(--surface); border: 1px solid var(--border); border-radius: 20px;
    width: 100%; max-width: 580px; max-height: 90vh;
    box-shadow: var(--shadow-xl);
    animation: mSlideUp .2s cubic-bezier(.22,.61,.36,1);
    display: flex; flex-direction: column;
  }
  .view-modal-hero {
    background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
    border-radius: 20px 20px 0 0; padding: 1.7rem 1.9rem 1.5rem;
    position: relative; overflow: hidden; flex-shrink: 0;
  }
  .view-modal-hero::after {
    content: '🔗'; position: absolute; right: 1.5rem; top: 50%; transform: translateY(-50%);
    font-size: 4rem; opacity: .1; line-height: 1; pointer-events: none;
  }
  .view-hero-top { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: .55rem; }
  .view-hero-name { font-size: 1.2rem; font-weight: 700; color: #fff; margin: 0 0 .28rem; }
  .view-hero-type {
    font-family: 'DM Mono', monospace; font-size: .68rem; font-weight: 500;
    background: rgba(255,255,255,.18); color: #fff;
    padding: .18rem .52rem; border-radius: 5px; display: inline-block;
  }
  .view-hero-project { font-size: .84rem; color: rgba(255,255,255,.75); margin-top: .3rem; display: flex; align-items: center; gap: .4rem; }
  .view-modal-body { padding: 1.5rem 1.9rem; overflow-y: auto; flex: 1; min-height: 0; scrollbar-width: thin; scrollbar-color: #cbd5e1 transparent; }
  .view-modal-body::-webkit-scrollbar { width: 5px; }
  .view-modal-body::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
  .view-section { margin-bottom: 1.5rem; }
  .view-section:last-child { margin-bottom: 0; }
  .view-section-title {
    font-size: .7rem; font-weight: 700; letter-spacing: .09em; text-transform: uppercase;
    color: var(--text-muted); margin-bottom: .9rem;
    display: flex; align-items: center; gap: .5rem;
  }
  .view-section-title::after { content: ''; flex: 1; height: 1px; background: var(--border); }
  .view-grid { display: grid; grid-template-columns: 1fr 1fr; gap: .7rem; }
  .view-field { display: flex; flex-direction: column; gap: .28rem; }
  .view-field.full { grid-column: 1 / -1; }
  .view-field-label { font-size: .71rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; letter-spacing: .06em; }
  .view-field-value {
    font-size: .86rem; color: var(--text); font-weight: 500;
    background: var(--surface2); border: 1px solid var(--border);
    border-radius: 8px; padding: .52rem .8rem; word-break: break-all;
  }
  .view-field-value.mono { font-family: 'DM Mono', monospace; font-size: .79rem; color: #1e40af; }
  .view-field-value.masked { color: var(--text-muted); letter-spacing: .12em; }
  .view-emp-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 4px; }
  .view-emp-chip {
    display: inline-flex; align-items: center; padding: 4px 12px; border-radius: 100px;
    font-size: .74rem; font-weight: 600; background: var(--accent-light); color: var(--accent);
    border: 1px solid rgba(37,99,235,.2);
  }
  .view-modal-footer {
    padding: 1.05rem 1.9rem; border-top: 1px solid var(--border);
    display: flex; align-items: center; justify-content: space-between;
    background: var(--surface2); border-radius: 0 0 20px 20px; flex-shrink: 0;
  }
  .view-footer-meta { font-size: .78rem; color: var(--text-muted); }
  .view-footer-meta strong { color: var(--text); }

  @media (max-width: 768px) {
    .ei-content { padding: 1.25rem; }
    .ei-header { flex-direction: column; align-items: flex-start; gap: .75rem; }
    .modal-backdrop { padding: .75rem; }
  }
`;

/* ─── Component ─────────────────────────────────────────────────────────────── */
const AdminIntegrationConfig = () => {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [configsLoading, setConfigsLoading] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [loading, setLoading] = useState(false);

  const [integration, setIntegration] = useState({ apiBaseUrl: "", accessToken: "", teamworkUserId: "" });
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [showToken, setShowToken] = useState(false);
  const [pendingEmpIds, setPendingEmpIds] = useState(null);

  /* ── Auth guard ── */
  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
    if (!roles.includes("ROLE_ADMIN")) { toast.error("Not authorized."); navigate("/"); return; }
  }, [navigate]);

  /* ── Fetch configs ── */
  const fetchConfigs = () => {
    setConfigsLoading(true);
    fetch(`${API_BASE_URL}/api/teamwork/admin`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => setConfigs(Array.isArray(data) ? data : []))
      .catch(() => setConfigs([]))
      .finally(() => setConfigsLoading(false));
  };
  useEffect(() => { fetchConfigs(); }, []);

  /* ── Fetch projects ── */
  useEffect(() => {
    setProjectsLoading(true);
    fetch(`${API_BASE_URL}/api/projects/all`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setProjects(Array.isArray(data) ? data : []))
      .catch(() => setProjects([]))
      .finally(() => setProjectsLoading(false));
  }, []);

  /* ── Fetch employees on project change ── */
  useEffect(() => {
    if (!selectedProject) { setEmployees([]); setSelectedEmployees([]); return; }
    setEmployeesLoading(true);
    setSelectedEmployees([]);
    fetch(`${API_BASE_URL}/api/manageteam/project/${selectedProject}/employees`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setEmployees(list);
        setSelectedEmployees(list.map(emp => String(emp.id)));
      })
      .catch(() => setEmployees([]))
      .finally(() => setEmployeesLoading(false));
  }, [selectedProject]);

  /* ── Restore pending employee selection after load ── */
  useEffect(() => {
    if (!employeesLoading && pendingEmpIds !== null) {
      setSelectedEmployees(pendingEmpIds);
      setPendingEmpIds(null);
    }
  }, [employeesLoading]);

  /* ── Open / close modal ── */
  const openNew = () => {
    setEditId(null);
    setIntegration({ apiBaseUrl: "", accessToken: "", teamworkUserId: "" });
    setSelectedProject("");
    setSelectedEmployees([]);
    setPendingEmpIds(null);
    setShowToken(false);
    setShowModal(true);
  };

  const openEdit = (cfg) => {
    const savedIds = cfg.employeeIds ? cfg.employeeIds.split(",").filter(Boolean) : [];
    setPendingEmpIds(savedIds);
    setEditId(cfg.id);
    setIntegration({ apiBaseUrl: cfg.apiBaseUrl || "", accessToken: cfg.accessToken || "", teamworkUserId: cfg.teamworkUserId || "" });
    setSelectedProject(cfg.projectId ? String(cfg.projectId) : "");
    setShowToken(false);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditId(null);
    setPendingEmpIds(null);
    setIntegration({ apiBaseUrl: "", accessToken: "", teamworkUserId: "" });
    setSelectedProject("");
    setSelectedEmployees([]);
    setShowToken(false);
  };

  /* ── Save / Update ── */
  const handleSave = async (e) => {
    e.preventDefault();
    if (!integration.apiBaseUrl || !integration.accessToken) {
      toast.error("API URL and Token are required"); return;
    }
    if (!selectedProject) { toast.error("Please select a project"); return; }
    if (!selectedEmployees.length) { toast.error("Please select at least one employee"); return; }

    const isEdit = editId !== null;
    const url = isEdit ? `${API_BASE_URL}/api/teamwork/admin/${editId}` : `${API_BASE_URL}/api/teamwork/admin`;

    setLoading(true);
    try {
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          apiBaseUrl: integration.apiBaseUrl,
          accessToken: integration.accessToken,
          teamworkUserId: integration.teamworkUserId,
          projectId: selectedProject,
          employeeIds: selectedEmployees,
        }),
      });
      if (!res.ok) throw new Error();
      toast.success(isEdit ? "Integration updated successfully" : "Integration saved successfully");
      closeModal();
      fetchConfigs();
    } catch {
      toast.error("Error saving integration");
    } finally {
      setLoading(false);
    }
  };

  /* ── Delete ── */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this integration config?")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/teamwork/admin/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success("Config deleted");
      fetchConfigs();
    } catch {
      toast.error("Error deleting config");
    }
  };

  const projectLabel = (id) => {
    const p = projects.find(p => String(p.id) === String(id));
    return p ? (p.projectName || p.name) : (id ? `Project #${id}` : "—");
  };

  return (
    <>
      <style>{styles}</style>
      <div className="fixed-top"><AdminNavbar /></div>
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="ei-page">
        <div className="ei-content">

          {/* Header */}
          <div className="ei-header">
            <div className="ei-header-left">
              <div className="ei-header-icon">🔗</div>
              <div>
                <h4>Teamwork Integration Settings</h4>
                <p>Configure API connection &amp; project-employee scope</p>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: ".6rem" }}>
              <button className="btn-new" onClick={openNew}>+ New Integration</button>
              <button className="btn-back" onClick={() => navigate(-1)}>← Back</button>
            </div>
          </div>

          {/* Stats */}
          <div className="ei-stats">
            <div className="stat-chip">Total <strong>{configs.length}</strong></div>
            <div className="stat-chip">Projects mapped <strong style={{ color: "var(--accent)" }}>{new Set(configs.map(c => c.projectId)).size}</strong></div>
          </div>

          {/* Table or empty */}
          {configsLoading ? (
            <div className="ei-table-card" style={{ padding: "2rem", display: "flex", flexDirection: "column", gap: 12 }}>
              {[1, 2, 3].map(i => (
                <div key={i} style={{ height: 14, borderRadius: 6, background: "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.2s infinite" }} />
              ))}
            </div>
          ) : configs.length === 0 ? (
            <div className="ei-empty">
              <div className="ei-empty-icon">🔌</div>
              <h6>No integrations configured yet</h6>
              <p>Click <strong>+ New Integration</strong> to connect your first Teamwork workspace.</p>
            </div>
          ) : (
            <div className="ei-table-card">
              <div className="ei-table-scroll">
                <table className="ei-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>API Base URL</th>
                      <th>Token</th>
                      <th>Project</th>
                      <th>Employees</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {configs.map((cfg, index) => {
                      const empCount = cfg.employeeIds ? cfg.employeeIds.split(",").filter(Boolean).length : 0;
                      return (
                        <tr key={cfg.id}>
                          <td><span className="td-sno">{index + 1}</span></td>
                          <td><span className="td-url">{cfg.apiBaseUrl || "—"}</span></td>
                          <td><span className="td-token">{maskToken(cfg.accessToken)}</span></td>
                          <td style={{ fontWeight: 500 }}>{projectLabel(cfg.projectId)}</td>
                          <td>
                            <div className="td-badge">
                              <div className="td-badge-dot">{empCount}</div>
                              {empCount === 1 ? "employee" : "employees"}
                            </div>
                          </td>
                          <td>
                            <div className="row-actions">
                              <button className="btn-action" onClick={() => setViewItem(cfg)}>👁 View</button>
                              <button className="btn-action edit" onClick={() => openEdit(cfg)}>✏ Edit</button>
                              <button className="btn-action del" onClick={() => handleDelete(cfg.id)}>🗑 Delete</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="ei-table-footer">
                Showing {configs.length} integration{configs.length !== 1 ? "s" : ""}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ──────────────── VIEW MODAL ──────────────── */}
      {viewItem && (
        <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && setViewItem(null)}>
          <div className="view-modal-box" onClick={e => e.stopPropagation()}>

            <div className="view-modal-hero">
              <div className="view-hero-top">
                <div>
                  <div className="view-hero-name">{projectLabel(viewItem.projectId)}</div>
                  <div className="view-hero-type">TEAMWORK</div>
                </div>
                <button
                  className="modal-close"
                  style={{ background: "rgba(255,255,255,.15)", borderColor: "rgba(255,255,255,.3)", color: "#fff" }}
                  onClick={() => setViewItem(null)}
                >✕</button>
              </div>
              <div className="view-hero-project">
                📁 {projectLabel(viewItem.projectId)}
              </div>
            </div>

            <div className="view-modal-body">
              <div className="view-section">
                <div className="view-section-title">API Connection</div>
                <div className="view-grid">
                  <div className="view-field full">
                    <div className="view-field-label">API Base URL</div>
                    <div className="view-field-value mono">{viewItem.apiBaseUrl || "—"}</div>
                  </div>
                  <div className="view-field">
                    <div className="view-field-label">Access Token</div>
                    <div className="view-field-value masked">{maskToken(viewItem.accessToken)}</div>
                  </div>
                  <div className="view-field">
                    <div className="view-field-label">Teamwork User ID</div>
                    <div className="view-field-value mono">{viewItem.teamworkUserId || "—"}</div>
                  </div>
                </div>
              </div>

              <div className="view-section">
                <div className="view-section-title">
                  Employees ({viewItem.employeeIds ? viewItem.employeeIds.split(",").filter(Boolean).length : 0})
                </div>
                <ViewEmployeeChips
                  projectId={viewItem.projectId}
                  employeeIds={viewItem.employeeIds}
                  token={token}
                />
              </div>
            </div>

            <div className="view-modal-footer">
              <div className="view-footer-meta">
                <strong>{viewItem.employeeIds ? viewItem.employeeIds.split(",").filter(Boolean).length : 0}</strong> employee{viewItem.employeeIds?.split(",").filter(Boolean).length !== 1 ? "s" : ""} mapped
              </div>
              <div style={{ display: "flex", gap: ".5rem" }}>
                <button className="btn-primary-m" onClick={() => { setViewItem(null); openEdit(viewItem); }}>✏ Edit</button>
                <button className="btn-cancel-m" onClick={() => setViewItem(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ──────────────── NEW / EDIT MODAL ──────────────── */}
      {showModal && (
        <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>

            <div className="modal-header">
              <div className="modal-header-left">
                <div className="modal-icon-wrap">🔗</div>
                <div>
                  <div className="modal-title">{editId ? "Edit Integration" : "New Integration"}</div>
                  <div className="modal-subtitle">Teamwork · Configure API connection &amp; project scope</div>
                </div>
              </div>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>

            <div className="modal-body">
              <form id="tw-form" onSubmit={handleSave}>

                <div className="form-section">
                  <div className="form-section-label">API Connection</div>
                  <div className="form-row">
                    <div className="fg full">
                      <label className="form-label">API Base URL <span className="req">*</span></label>
                      <input
                        className="f-control"
                        type="text"
                        placeholder="https://your-company.teamwork.com"
                        value={integration.apiBaseUrl}
                        onChange={e => setIntegration({ ...integration, apiBaseUrl: e.target.value })}
                      />
                      <div className="form-hint">The root endpoint for all Teamwork API calls</div>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="fg half">
                      <label className="form-label">Access Token <span className="req">*</span></label>
                      <div className="f-token-wrap">
                        <input
                          className="f-control"
                          type={showToken ? "text" : "password"}
                          placeholder="Bearer eyJhbGci…"
                          value={integration.accessToken}
                          onChange={e => setIntegration({ ...integration, accessToken: e.target.value })}
                        />
                        <button type="button" className="f-token-toggle" onClick={() => setShowToken(v => !v)}>
                          {showToken ? "Hide" : "Show"}
                        </button>
                      </div>
                    </div>
                    <div className="fg half">
                      <label className="form-label">Teamwork User ID</label>
                      <input
                        className="f-control"
                        type="text"
                        placeholder="e.g. 789123"
                        value={integration.teamworkUserId}
                        onChange={e => setIntegration({ ...integration, teamworkUserId: e.target.value })}
                      />
                      {/* <div className="form-hint">Used to filter comment author and timelogs</div> */}
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <div className="form-section-label">Project &amp; Employees</div>
                  <div className="form-row">
                    <div className="fg full">
                      <label className="form-label">Select Project <span className="req">*</span></label>
                      <select
                        className="f-select"
                        value={selectedProject}
                        onChange={e => setSelectedProject(e.target.value)}
                        disabled={projectsLoading}
                      >
                        <option value="">— Choose a project —</option>
                        {projects.map(p => {
                          const label = p.projectName || p.name || `Project #${p.id}`;
                          return <option key={p.id} value={p.id} title={label}>{label}</option>;
                        })}
                      </select>
                    </div>
                  </div>

                  {selectedProject && (
                    <div className="fg full" style={{ marginTop: 4 }}>
                      <label className="form-label">
                        Employees
                        <span style={{ fontWeight: 400, color: "var(--text-muted)", marginLeft: 6, fontSize: ".74rem" }}>
                          {employeesLoading ? "Loading…" : `${selectedEmployees.length} of ${employees.length} selected`}
                        </span>
                      </label>
                      {!employeesLoading && employees.length > 0 && (
                        <div className="emp-select-actions">
                          <button type="button" className="emp-sel-btn" onClick={() => setSelectedEmployees(employees.map(e => String(e.id)))}>Select All</button>
                          <button type="button" className="emp-sel-btn desel" onClick={() => setSelectedEmployees([])}>Deselect All</button>
                        </div>
                      )}
                      <div className="emp-chip-list">
                        {employeesLoading && <span style={{ fontSize: ".78rem", color: "var(--text-muted)" }}>Loading members…</span>}
                        {!employeesLoading && employees.length === 0 && (
                          <span style={{ fontSize: ".78rem", color: "var(--text-muted)" }}>No employees found for this project</span>
                        )}
                        {!employeesLoading && employees.map(emp => {
                          const name = empName(emp);
                          const isSelected = selectedEmployees.includes(String(emp.id));
                          return (
                            <div
                              key={emp.id}
                              className={`emp-chip ${isSelected ? "selected" : ""}`}
                              onClick={() =>
                                setSelectedEmployees(prev =>
                                  isSelected ? prev.filter(id => id !== String(emp.id)) : [...prev, String(emp.id)]
                                )
                              }
                              title={isSelected ? `Deselect ${name}` : `Select ${name}`}
                            >
                              <span className="emp-avatar">{initials(name)}</span>
                              {name}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

              </form>
            </div>

            <div className="modal-footer">
              <button type="submit" form="tw-form" className="btn-primary-m" disabled={loading}>
                {loading && <span className="spinner" />}
                {loading ? "Saving…" : editId ? "Save " : "Save"}
              </button>
              <button type="button" className="btn-cancel-m" onClick={closeModal}>Cancel</button>
            </div>

          </div>
        </div>
      )}
    </>
  );
};

/* ─── Sub-component: resolves employee IDs → names for the view modal ──────── */
const ViewEmployeeChips = ({ projectId, employeeIds, token }) => {
  const [empMap, setEmpMap] = useState({});

  useEffect(() => {
    if (!projectId) return;
    fetch(`${API_BASE_URL}/api/manageteam/project/${projectId}/employees`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) {
          const map = {};
          data.forEach(emp => { map[String(emp.id)] = empName(emp); });
          setEmpMap(map);
        }
      })
      .catch(() => { });
  }, [projectId]);

  const ids = employeeIds ? employeeIds.split(",").filter(Boolean) : [];
  if (ids.length === 0) return <span style={{ fontSize: ".78rem", color: "var(--text-muted)" }}>None</span>;

  return (
    <div className="view-emp-chips">
      {ids.map(id => (
        <span key={id} className="view-emp-chip">
          {empMap[id] || `ID: ${id}`}
        </span>
      ))}
    </div>
  );
};

export default AdminIntegrationConfig;
